import React from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  ListItemButton, 
  IconButton, 
  Box, 
  Divider, 
  useTheme, 
  useMediaQuery,
  Button,
  CircularProgress,
  Backdrop
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AppsIcon from '@mui/icons-material/Apps';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashboardIcon from '@mui/icons-material/Dashboard';

const AppNavigation = ({ 
  selectedApp, 
  setSelectedApp, 
  showAllApps, 
  setShowAllApps, 
  APP_LIST,
  handleLogout,
  setLoading,
  loading
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    if (!loading) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleAppSelect = (appId) => {
    if (!loading) {
      setSelectedApp(appId);
      setShowAllApps(false);
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  };

  const handleAllAppsSelect = () => {
    if (!loading) {
      setShowAllApps(true);
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Content Section */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">App Analytics</Typography>
        </Box>
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton selected={showAllApps} onClick={handleAllAppsSelect} disabled={loading}>
              <ListItemIcon>
                <DashboardIcon color={showAllApps ? "primary" : "inherit"} />
              </ListItemIcon>
              <ListItemText primary="All Apps Dashboard" />
            </ListItemButton>
          </ListItem>
        </List>
        <Divider />
        <List>
          <ListItem>
            <ListItemIcon>
              <AppsIcon />
            </ListItemIcon>
            <ListItemText primary="Apps" />
          </ListItem>
          {APP_LIST.map((app) => (
            <ListItem key={app.id} disablePadding>
              <ListItemButton
                selected={selectedApp === app.id && !showAllApps}
                onClick={() => handleAppSelect(app.id)}
                disabled={loading}
              >
                <ListItemIcon>
                  <BarChartIcon
                    color={selectedApp === app.id && !showAllApps ? "primary" : "inherit"}
                  />
                </ListItemIcon>
                <ListItemText primary={app.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      
      {/* Fixed Logout Button at the Bottom */}
      <Button
        variant="outlined"
        color="primary"
        onClick={handleLogout}
        sx={{ marginTop: 'auto' , margin: '5px'}}
        disabled={loading}
      >
        Logout
      </Button>
    </Box>
  );

  const drawerWidth = 240;

  return (
    <>
        <>
          {/* AppBar for mobile */}
          {isMobile && (
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
              <Toolbar>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2 }}
                  disabled={loading}
                >
                  <MenuIcon />
                </IconButton>
                <Typography variant="h6" noWrap component="div">
                  {showAllApps
                    ? 'All Apps Dashboard'
                    : `${APP_LIST.find((app) => app.id === selectedApp)?.name} Dashboard`}
                </Typography>
              </Toolbar>
            </AppBar>
          )}
  
          {/* Side Drawer */}
          <Box
            component="nav"
            sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
          >
            {isMobile ? (
              <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true, disableEscapeKeyDown: loading, disableBackdropClick: loading }}
                sx={{
                  '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                }}
              >
                {drawer}
              </Drawer>
            ) : (
              <Drawer
                variant="permanent"
                open
                sx={{
                  '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                }}
              >
                {drawer}
              </Drawer>
            )}
          </Box>
  
          {/* Main content spacer for mobile */}
          {isMobile && <Toolbar />}
        </>
    </>
  );
};

export default AppNavigation;